import { SBCRewardType } from "@/api/privateRequests/sbcs/getSbcSets";
import { RewardBand } from "@/components/organisms/SBCs/RewardBand";
import { TradeableChip } from "@/components/organisms/SBCs/TradeableChip";
import { extractRewardUrl } from "@/functions/extractRewardUrl";
import { tierSelector } from "@/redux/players/playersSlice";
import { ReactNode } from "react";
import { useSelector } from "react-redux";

/**
 * A reward's art with its name and tradeability, stacked to fit the rail. The
 * chip is the set card's, so the two read as the same thing in two places; the
 * pack blurb is left to the set card, which has the width for it.
 */
export const RewardBlock = ({ reward }: { reward: SBCRewardType }) => {
  // A player reward without card data (the single-set endpoint omits it) shows
  // its art, so the name below is the only thing naming it.
  const isPlayer = reward.type === "player";
  const isEventToken = reward.type === "event_token_2";
  const name = reward.name || reward.shortName;

  return (
    /* The set card's band, narrowed to the rail: art on the left, the chip and
       the name stacked beside it. */
    <div className="flex items-center gap-x-4">
      <img
        loading="lazy"
        className={`shrink-0 drop-shadow-[0_8px_16px_rgba(0,0,0,0.5)] ${
          isEventToken ? "w-14" : isPlayer ? "w-24" : "w-20"
        }`}
        alt={name || ""}
        src={extractRewardUrl(reward)}
      />
      <div className="flex min-w-0 flex-1 flex-col gap-y-1.5">
        <TradeableChip
          rewardType={reward.type}
          untradeable={reward.untradeable}
        />
        {name && (
          <p className="text-sm font-semibold text-foreground-body">{name}</p>
        )}
      </div>
    </div>
  );
};

/**
 * The set's payout, whichever shape it takes: a player reward the store has the
 * card data for as the SBC card's band (card, then its own label, rating and
 * blurb beside it), everything else as the art block under `label`.
 */
export const SolutionReward = ({
  reward,
  description,
  label,
}: {
  reward: SBCRewardType;
  description?: string;
  label: ReactNode;
}) => {
  const tier = useSelector(
    tierSelector(
      reward.metaRating?.metarating || 0,
      reward.metaRating?.playerRoleId,
      true,
    ),
  );

  return reward.type === "player" && reward.player ? (
    <RewardBand
      reward={reward}
      rewardUrl={extractRewardUrl(reward)}
      description={description}
      tier={tier}
      className="mt-0 p-0"
    />
  ) : (
    <div className="flex flex-col gap-3">
      {label}
      <RewardBlock reward={reward} />
    </div>
  );
};
